import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import "../components/css/AdminProjectCustomers.css";

axios.defaults.withCredentials = true;

const AdminProjectCustomerDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [detail, setDetail] = useState(null);
  const [formData, setFormData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showSerialHistory, setShowSerialHistory] = useState(false);
  const maTypeOptions = ["3rd Party", "Vender Direct"];
  const assetTypeOptions = ["Hardware", "Software"];
  const slaOptions = ["7x24x4", "7x24xNBD", "5x8xNBD"];

  const contractNumberPattern = /^CT\d{6}-\d{3}$/;
  const formatDateForInput = useCallback((value) => {
    if (!value) return "";

    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "";

    return date.toISOString().slice(0, 10);
  }, []);

  const normalizeFormData = useCallback(
    (data) => {
      return {
        ...data,
        current_end_date: formatDateForInput(data?.current_end_date),
      };
    },
    [formatDateForInput],
  );

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const res = await axios.get(
          `/project-customers/${id}`,
          {
            withCredentials: true,
          },
        );

        setDetail(res.data || null);
        const data = res.data || {};

        const normalizedData = normalizeFormData(data);

        setDetail(normalizedData);
        setFormData(normalizedData);
      } catch (err) {
        console.error("fetchDetail error:", err);
        setDetail(null);
        setFormData({});
      }
    };

    if (id) {
      fetchDetail();
    }
  }, [id, normalizeFormData]);

  const formatDateDisplay = (value) => {
    if (!value) return "";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "";

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    const normalizedValue =
      name === "contract_no" ? value.toUpperCase() : value;

    setFormData((prev) => ({
      ...prev,
      [name]: normalizedValue,
    }));
  };

  const handleEdit = () => {
    setFormData((prev) => normalizeFormData(prev));
    setIsEditing(true);
  };

  const handleCancel = () => {
    setFormData(normalizeFormData(detail || {}));
    setIsEditing(false);
  };

  const requiredFields = [
    { key: "contract_no", label: "Contract Number" },
    { key: "type", label: "Type" },
    { key: "prd_serial", label: "Serial Number" },
    { key: "prd_label", label: "Product" },
    { key: "current_sla", label: "Current SLA" },
    { key: "current_end_date", label: "Current End Date" },
    { key: "location", label: "Location" },
    { key: "current_ma_type", label: "Current MA Type" },
  ];

  const isBlank = (value) => {
    return value === undefined || value === null || String(value).trim() === "";
  };

  const handleSave = () => {
    const missingFields = requiredFields.filter((field) =>
      isBlank(formData[field.key]),
    );

    if (missingFields.length > 0) {
      alert(
        `กรุณากรอกข้อมูลให้ครบถ้วน:\n- ${missingFields
          .map((field) => field.label)
          .join("\n- ")}`,
      );
      return;
    }
    const normalizedContractNo = String(formData.contract_no || "")
      .trim()
      .toUpperCase();

    if (!contractNumberPattern.test(normalizedContractNo)) {
      alert("Contract Number ต้องอยู่ในรูปแบบ CTxxxxxx-xxx เช่น CT202607-034");
      return;
    }

    if (!assetTypeOptions.includes(String(formData.type || "").trim())) {
      alert("Type ต้องเป็น Hardware หรือ Software เท่านั้น");
      return;
    }
    setShowSaveConfirm(true);
  };

  const confirmSave = async () => {
    try {
      setShowSaveConfirm(false);

      const payload = {
        contract_no: String(formData.contract_no || "")
          .trim()
          .toUpperCase(),
        type: String(formData.type || "").trim(),
        prd_label: formData.prd_label,
        prd_serial: formData.prd_serial,
        current_ma_type: formData.current_ma_type,
        current_sla: formData.current_sla,
        current_end_date: formData.current_end_date,
        location: formData.location,
      };

      await axios.put(
        `/project-customers/${id}`,
        payload,
        {
          withCredentials: true,
        },
      );

      const normalizedPayload = normalizeFormData({
        ...formData,
        ...payload,
      });

      setDetail(normalizedPayload);
      setFormData(normalizedPayload);
      setIsEditing(false);

      setSuccessMessage("บันทึกข้อมูลสำเร็จ");
      setShowSaveSuccess(true);
    } catch (err) {
      console.error("update error:", err);
      alert(err.response?.data?.message || "Update failed");
    }
  };

  const cancelSave = () => {
    setShowSaveConfirm(false);
  };

  return (
    <>
      <div className="dash-container">
        <div className="apc-detail-card">
          <div className="apc-detail-header">
            <h2 className="apc-title">Customer Asset Detail</h2>

            <div className="apc-detail-actions">
              {!isEditing ? (
                <button
                  type="button"
                  className="apc-edit-btn"
                  onClick={handleEdit}
                >
                  Edit
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="apc-save-btn"
                    onClick={handleSave}
                  >
                    Save
                  </button>

                  <button
                    type="button"
                    className="apc-cancel-btn"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                </>
              )}
              <button
                type="button"
                className="back-btn"
                onClick={() => navigate("/admin/customer-assets")}
              >
                Back
              </button>
            </div>
          </div>

          <div className="apc-detail-grid">
            <div className="apc-field">
              <label>Company Name</label>
              <input
                type="text"
                name="cus_name"
                value={formData?.cus_name || ""}
                onChange={handleChange}
                readOnly
              />
            </div>

            <div className="apc-field">
              <label>Customer Name</label>
              <input
                type="text"
                name="customer_name"
                value={formData?.customer_name || ""}
                onChange={handleChange}
                readOnly
              />
            </div>

            <div className="apc-field">
              <label>Phone</label>
              <input
                type="text"
                name="phone"
                value={formData?.phone || ""}
                onChange={handleChange}
                readOnly
              />
            </div>

            <div className="apc-field">
              <label>Email</label>
              <input
                type="text"
                name="email"
                value={formData?.email || ""}
                onChange={handleChange}
                readOnly
              />
            </div>

            <div className="apc-field">
              <label>Contract Number</label>

              <input
                type="text"
                name="contract_no"
                value={formData?.contract_no || ""}
                onChange={handleChange}
                readOnly={!isEditing}
                placeholder="CT202607-034"
                maxLength={12}
                required
              />
            </div>

            <div className="apc-field">
              <label>Type</label>

              <select
                name="type"
                value={formData?.type || ""}
                onChange={handleChange}
                disabled={!isEditing}
                required
              >
                <option value="">Select Type</option>

                {assetTypeOptions.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>

            <div className="apc-field">
              <label>Serial Number</label>
              <input
                type="text"
                name="prd_serial"
                value={formData?.prd_serial || ""}
                onChange={handleChange}
                readOnly={!isEditing}
                required
              />
            </div>

            <div className="apc-field">
              <label>Product</label>
              <input
                type="text"
                name="prd_label"
                value={formData?.prd_label || ""}
                onChange={handleChange}
                readOnly={!isEditing}
                required
              />
            </div>

            <div className="apc-field">
              <label>Current SLA</label>
              <select
                name="current_sla"
                value={formData?.current_sla || ""}
                onChange={handleChange}
                disabled={!isEditing}
              >
                {slaOptions.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>

            <div className="apc-field">
              <label>Current End Date</label>
              {isEditing ? (
                <input
                  type="date"
                  name="current_end_date"
                  value={formData?.current_end_date || ""}
                  onChange={handleChange}
                  required
                />
              ) : (
                <input
                  type="text"
                  value={formatDateDisplay(formData?.current_end_date)}
                  readOnly
                  required
                />
              )}
            </div>

            <div className="apc-field">
              <label>Current MA Type</label>
              <select
                name="current_ma_type"
                value={formData?.current_ma_type || ""}
                onChange={handleChange}
                disabled={!isEditing}
                required
              >
                {maTypeOptions.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>

            <div className="apc-field">
              <label>Location</label>
              <textarea
                name="location"
                value={formData?.location || ""}
                onChange={handleChange}
                readOnly={!isEditing}
                required
              />
            </div>
          </div>
        </div>

        <div className="apc-history-card">
          <div className="apc-history-header">
            <div>
              <h3>Serial Change History</h3>
              <p>
                ประวัติการเปลี่ยนเครื่องหรือเปลี่ยน Serial ใหม่
                {detail?.serialChangeLogs?.length > 0
                  ? ` (${detail.serialChangeLogs.length} รายการ)`
                  : ""}
              </p>
            </div>

            <button
              type="button"
              className="apc-history-toggle-btn"
              onClick={() => setShowSerialHistory((prev) => !prev)}
            >
              {showSerialHistory ? "ซ่อนประวัติ" : "แสดงประวัติ"}
            </button>
          </div>

          {showSerialHistory && (
            <>
              {detail?.serialChangeLogs?.length > 0 ? (
                <div className="apc-history-list">
                  {detail.serialChangeLogs.map((log) => {
                    const isInitialImport = log.log_type === "INITIAL_IMPORT";
                    const isAssetInfoUpdated =
                      log.log_type === "ASSET_INFO_UPDATED";
                    const isSerialChangedByTicket =
                      log.log_type === "SERIAL_CHANGED_BY_TICKET";

                    return (
                      <div key={log.id} className="apc-history-item">
                        <div className="apc-history-main">
                          <strong>
                            {isInitialImport
                              ? "เพิ่ม Serial เข้าระบบครั้งแรก"
                              : isAssetInfoUpdated
                                ? "มีการอัปเดตข้อมูล Asset"
                                : "มีการเปลี่ยนเครื่อง / เปลี่ยน Serial ใหม่"}
                          </strong>

                          {log.log_serial && (
                            <span>
                              Serial:{" "}
                              <strong className="apc-history-serial">
                                {log.log_serial}
                              </strong>
                            </span>
                          )}
                        </div>

                        <div className="apc-history-detail">
                          {isInitialImport && (
                            <span>Import เข้าระบบครั้งแรก</span>
                          )}

                          {isAssetInfoUpdated && (
                            <>
                              {log.current_ma_type && (
                                <span>
                                  Current MA Type: {log.current_ma_type}
                                </span>
                              )}

                              {log.current_sla && (
                                <span>Current SLA: {log.current_sla}</span>
                              )}

                              {log.current_end_date && (
                                <span>
                                  Current End Date:{" "}
                                  {formatDateDisplay(log.current_end_date)}
                                </span>
                              )}
                            </>
                          )}

                          {isSerialChangedByTicket && (
                            <>
                              <span>
                                Ticket:{" "}
                                <button
                                  type="button"
                                  className="apc-ticket-link-btn"
                                  onClick={() =>
                                    navigate(
                                      `/admin/ticketadmin/${log.ticket_id}`,
                                    )
                                  }
                                >
                                  #{log.ticket_id}
                                </button>
                              </span>

                              <span>
                                Issue: {log.ticket_issue_summary || "-"}
                              </span>
                            </>
                          )}

                          <span>Changed By Admin: {log.changed_by || "-"}</span>
                        </div>

                        <p className="apc-history-message">
                          {isInitialImport
                            ? "ระบบบันทึก Serial นี้จากการ Import ข้อมูลเข้าระบบครั้งแรก"
                            : isAssetInfoUpdated
                              ? "ระบบบันทึกว่ามีการอัปเดตข้อมูลของ Customer Asset"
                              : `ระบบบันทึกว่ามีการเปลี่ยนเครื่องหรือเปลี่ยน Serial ใหม่ จากการดำเนินการผ่าน Ticket #${log.ticket_id}`}
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="apc-history-empty">
                  ยังไม่มีประวัติการเปลี่ยน Serial
                </div>
              )}
            </>
          )}
        </div>

        {showSaveConfirm && (
          <div className="apc-modal-overlay">
            <div className="apc-confirm-modal">
              <h3>ยืนยันการบันทึก</h3>

              <p>ต้องการบันทึกการแก้ไขข้อมูล Customer Asset นี้ใช่หรือไม่?</p>

              <div className="apc-confirm-actions">
                <button
                  type="button"
                  className="apc-confirm-save-btn"
                  onClick={confirmSave}
                >
                  ยืนยันบันทึก
                </button>

                <button
                  type="button"
                  className="apc-confirm-cancel-btn"
                  onClick={cancelSave}
                >
                  ยกเลิก
                </button>
              </div>
            </div>
          </div>
        )}
        {showSaveSuccess && (
          <div className="apc-modal-overlay">
            <div className="apc-success-modal">
              <div className="apc-success-icon">✓</div>

              <h3>บันทึกสำเร็จ</h3>

              <p>{successMessage || "บันทึกข้อมูลสำเร็จ"}</p>

              <div className="apc-confirm-actions">
                <button
                  type="button"
                  className="apc-confirm-save-btn"
                  onClick={() => setShowSaveSuccess(false)}
                >
                  ตกลง
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminProjectCustomerDetailPage;
